import React, { useState } from "react";
import { ChevronDown, ChevronRight, History, Clock3 } from "lucide-react";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatCst = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Collapsed-by-default day save history for Cash Box / Tournament Chips.
 */
export function SaveLogsPanel({ logs = [], isLoading = false, title = "Save Logs" }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between bg-gray-50 px-5 py-4 text-left transition hover:bg-yellow-50"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-yellow-200 bg-yellow-100 text-yellow-800">
            <History className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-800">{title}</p>
            <p className="text-xs text-gray-500">
              Every Starting / Closing save for this date
            </p>
          </div>
          <span className="rounded-full bg-yellow-400 px-2.5 py-0.5 text-xs font-bold text-black">
            {logs.length}
          </span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-2">
          {isLoading ? (
            <p className="py-4 text-sm text-gray-500">Loading logs…</p>
          ) : logs.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
              <p className="text-sm text-gray-500">
                No saves for this date yet. Save Starting or Closing to create
                the first log.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {logs.map((log) => {
                const isExpanded = expandedId === log.id;
                const diff = Number(log.difference || 0);
                const isClosing = log.action === "closing";
                return (
                  <li key={log.id} className="py-3">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId(isExpanded ? null : log.id)
                      }
                      className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left transition hover:bg-gray-50"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                              isClosing
                                ? "bg-blue-100 text-blue-800"
                                : "bg-emerald-100 text-emerald-800"
                            }`}
                          >
                            {isClosing ? "Closing" : "Starting"}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Clock3 className="h-3 w-3" />
                            {formatCst(log.created_at)}
                          </span>
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500">
                          Saved by{" "}
                          <span className="font-medium text-gray-700">
                            {log.saved_by_name || log.saved_by || "Unknown"}
                          </span>
                        </p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-gray-600">
                          Start{" "}
                          <span className="font-semibold text-gray-800">
                            ${money(log.starting_total)}
                          </span>
                        </p>
                        <p className="text-gray-600">
                          Close{" "}
                          <span className="font-semibold text-gray-800">
                            ${money(log.closing_total)}
                          </span>
                        </p>
                        <p
                          className={`mt-0.5 font-bold ${
                            diff > 0
                              ? "text-emerald-700"
                              : diff < 0
                                ? "text-red-700"
                                : "text-gray-600"
                          }`}
                        >
                          Diff {diff > 0 ? "+" : ""}${money(diff)}
                        </p>
                      </div>
                    </button>

                    {isExpanded && log.snapshot && (
                      <div className="mt-2 rounded-md border border-yellow-100 bg-yellow-50 px-3 py-3 text-xs text-gray-700">
                        {log.snapshot.startingCashTotal != null && (
                          <p>
                            Starting cash $
                            {money(log.snapshot.startingCashTotal)} · chips $
                            {money(log.snapshot.startingChipsTotal)}
                          </p>
                        )}
                        {log.snapshot.closingCashTotal != null && (
                          <p className="mt-1">
                            Closing cash $
                            {money(log.snapshot.closingCashTotal)} · chips $
                            {money(log.snapshot.closingChipsTotal)}
                          </p>
                        )}
                        {log.snapshot.startingChipsTotal != null &&
                          log.snapshot.startingCashTotal == null && (
                            <p>
                              Starting chips $
                              {money(log.snapshot.startingChipsTotal)}
                            </p>
                          )}
                        {log.snapshot.closingChipsTotal != null &&
                          log.snapshot.closingCashTotal == null && (
                            <p className="mt-1">
                              Closing chips $
                              {money(log.snapshot.closingChipsTotal)}
                            </p>
                          )}
                        {log.snapshot.rolledIntoNextDay && (
                          <p className="mt-2 font-semibold text-yellow-900">
                            Closing was rolled into Starting / next day.
                          </p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
